import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  Truck, 
  Bus, 
  Van, 
  Warning,
  Gavel,
  Info,
  SpinnerGap,
  BookOpen
} from '@phosphor-icons/react';
import axios from 'axios';
import { BookmarkButton } from '../components/BookmarkButton';
import { OGLAttribution } from '../components/OGLAttribution';

const API = process.env.REACT_APP_BACKEND_URL;

const getSeverityClass = (severity) => {
  switch (severity?.toUpperCase()) {
    case 'MSI':
      return 'severity-serious';
    case 'VSI':
      return 'severity-significant';
    case 'SI':
      return 'severity-significant';
    case 'STANDARD':
      return 'severity-minor';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const getSeverityLabel = (severity) => {
  switch (severity?.toUpperCase()) {
    case 'MSI':
      return 'Most Serious Infringement';
    case 'VSI':
      return 'Very Serious Infringement';
    case 'SI':
      return 'Serious Infringement';
    case 'STANDARD':
      return 'Standard';
    default:
      return severity || 'Unknown';
  }
};

const getVehicleIcon = (type) => {
  switch (type?.toUpperCase()) {
    case 'HGV':
      return <Truck weight="duotone" className="h-5 w-5" />;
    case 'PSV':
      return <Bus weight="duotone" className="h-5 w-5" />;
    case 'LGV':
      return <Van weight="duotone" className="h-5 w-5" />;
    default:
      return <Truck weight="duotone" className="h-5 w-5" />;
  }
};

export const SanctionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [sanction, setSanction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSanction = async () => {
      try {
        const { data } = await axios.get(`${API}/api/sanctions/${id}`);
        setSanction(data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load sanction');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSanction();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <SpinnerGap weight="bold" className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <Warning weight="duotone" className="h-16 w-16 mx-auto text-destructive mb-4" />
          <h1 className="text-2xl font-semibold mb-2">Error Loading Sanction</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-sm">
            <ArrowLeft weight="bold" className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="rounded-sm -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft weight="bold" className="mr-2 h-4 w-4" />
            Back to Search
          </Button>
        </div>
        
        {/* Main Card */}
        <div className="border border-border bg-background">
          {/* Header */}
          <div className="p-6 sm:p-8 border-b border-border">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${getSeverityClass(sanction.severity)}`}>
                  {getSeverityLabel(sanction.severity)}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium bg-muted text-muted-foreground rounded-full">
                  {getVehicleIcon(sanction.vehicle_type)}
                  {sanction.vehicle_type}
                </span>
              </div>
              <BookmarkButton sanctionId={sanction.id} variant="full" />
            </div>
            
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" data-testid="sanction-title">
              {sanction.offence_title}
            </h1>
            
            <Badge variant="outline" className="rounded-sm">
              <BookOpen weight="duotone" className="mr-1 h-3 w-3" />
              {sanction.category}
            </Badge>
          </div>
          
          {/* Description */}
          <div className="p-6 sm:p-8 border-b border-border">
            <h2 className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <Info weight="duotone" className="h-4 w-4" />
              Description
            </h2>
            <p className="text-foreground leading-relaxed" data-testid="sanction-description">
              {sanction.description}
            </p>
          </div>
          
          {/* Likely Outcome */}
          <div className="p-6 sm:p-8 border-b border-border bg-muted/30">
            <h2 className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <Gavel weight="duotone" className="h-4 w-4" />
              Likely DVSA Action
            </h2>
            <p className="text-foreground leading-relaxed font-medium" data-testid="sanction-outcome">
              {sanction.likely_outcome}
            </p>
            {sanction.enforcement_action && (
              <p className="mt-3 text-sm text-muted-foreground">
                <span className="font-medium">Enforcement Code:</span> {sanction.enforcement_action}
              </p>
            )}
          </div>
          
          {/* Regulatory Reference */}
          {sanction.regulatory_reference && (
            <div className="p-6 sm:p-8 border-b border-border">
              <h2 className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <BookOpen weight="duotone" className="h-4 w-4" />
                Regulatory Reference
              </h2>
              <p className="text-sm text-foreground leading-relaxed" data-testid="sanction-reference">
                {sanction.regulatory_reference}
              </p>
            </div>
          )}
          
          {/* Notes */}
          {sanction.notes && (
            <div className="p-6 sm:p-8">
              <h2 className="text-xs uppercase tracking-[0.2em] font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <Warning weight="duotone" className="h-4 w-4" />
                Additional Notes
              </h2>
              <p className="text-muted-foreground leading-relaxed" data-testid="sanction-notes">
                {sanction.notes}
              </p>
            </div>
          )}

          {/* Policy provenance */}
          {sanction.policy_version && (
            <div className="p-4 sm:p-6 border-t border-border bg-muted/30">
              <p className="text-xs text-muted-foreground">
                Source: DVSA Enforcement Sanctions Policy — <strong>{sanction.policy_version}</strong>
                {sanction.policy_source_url && (
                  <> · <a href={sanction.policy_source_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground" data-testid="policy-source-link">view official PDF</a></>
                )}
              </p>
            </div>
          )}
        </div>
        
        {/* Related Actions */}
        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <Link to={`/dashboard?category=${encodeURIComponent(sanction.category)}`} className="flex-1">
            <Button variant="outline" className="w-full rounded-sm h-12">
              View all {sanction.category} offences
            </Button>
          </Link>
          <Link to={`/dashboard?severity=${encodeURIComponent(sanction.severity)}`} className="flex-1">
            <Button variant="outline" className="w-full rounded-sm h-12">
              View all {sanction.severity} offences
            </Button>
          </Link>
        </div>
      </main>
      
      {/* Footer Disclaimer */}
      <footer className="border-t border-border mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-3">
          <p className="text-xs text-muted-foreground text-center">
            This tool is for guidance only and does not constitute legal advice. Based on DVSA Enforcement Sanctions Policy (May 2026).
          </p>
          <OGLAttribution className="text-center" />
        </div>
      </footer>
    </div>
  );
};
